import React, {CSSProperties} from "react";
import {Button, Col, Nav, Navbar} from "react-bootstrap";
import {connect, ConnectedProps} from "react-redux";
import {GeneralActions} from "../../store/actions/GeneralActions";
import StateType from "../../store/store";

import styles from './Header.module.scss';
import ContactBanner from "../ContactBanner/ContactBanner";
import HeaderLogo from "./HeaderLogo/HeaderLogo";
import {useLocation, useNavigate} from "react-router-dom";
import NotificationCenter from "./NotificationCenter/NotificationCenter";
import {CarrierHeaderBrandingDocument} from "../../BrandingTypes";
import ConditionalComponent from "../../hoc/ConditionalComponent/ConditionalComponent";
import ProfileDropdown from "./ProfileDropdown/ProfileDropdown";
import { useCookies } from "react-cookie";
import * as config from "../../config";

const map_state_to_props = (state: StateType) => ({
    state: {
        is_guest: state.account.is_guest,
        branding_header: state.general.carrier_branding?.header,
        user_data: state.account.user_data,
        branding_language: state.branding.branding_language,
        is_reguser: state.account.is_reguser,
        is_admin: state.account.is_admin,
        is_employee: state.account.is_employee
    }
});

const map_dispatch_to_props = (dispatch: any) => ({
    actions: {
        show_mobile_nav: () => dispatch(GeneralActions.show_mobile_nav())
    }
});

const connector = connect(map_state_to_props, map_dispatch_to_props);

type BrandingLogoProps = {
    branding_header: CarrierHeaderBrandingDocument | undefined;
    is_guest: boolean;
};

const BrandingLogo = ({branding_header, is_guest}: BrandingLogoProps) => {
    if (!branding_header || branding_header.logo === 'default') return (
        <HeaderLogo logo={{
            type: 'img',
            alt: "Consent On Demand Logo",
            target: is_guest ? undefined : "/",
            src: '/images/Consent-On-Demand-logo-White-RGB-72.png'
        }}/>
    );
    if (!branding_header.logo) return null;
    const logo = branding_header.logo.filter(logo => {
        switch (logo.visibility) {
            case 'llp':
                return is_guest;
            case "system":
                return !is_guest;
            case "both":
                return true;
        }
    });
    return (
        <>
            {logo.map((logo, index) => (
                <HeaderLogo
                    key={index}
                    logo={logo}
                    text_color={branding_header.colors?.links}
                />
            ))}
        </>
    );
};

const Header = ({state, actions}: ConnectedProps<typeof connector>) => {

    const location = useLocation();
    const navigate = useNavigate();

    const [cookies, setCookie, removeCookie] = useCookies();
    const is_initial_login = cookies[config.INITIAL_LOGIN_TOKEN_COOKIE_NAME];

    const branding_styles: CSSProperties = {};
    const default_header_paths = [
        '/disclosure'
    ];

    const default_header_paths_found = (default_header_paths.findIndex(path => location.pathname.indexOf(path) >= 0) >= 0)

    if (state.branding_header && !default_header_paths_found) {
        if (state.branding_header.colors.background) {
            branding_styles.backgroundColor = `${state.branding_header.colors.background}`
        }
    }

    const hidden_header_paths = [
        '/login',
        '/forgot_password',
        '/forgot_username',
        '/expired',
        '/forgot',
        '/registration',
        '/select_profile',
        '/tou',
        '/pp',
        '/association',
        '/create_account',
        '/invitation',
        '/lp'
    ];
    if (hidden_header_paths.findIndex(path => location.pathname.indexOf(path) >= 0) >= 0) return null;
    if (is_initial_login === 'true' && !state.is_employee) return null;

    const show_toggle = (() => {
        if (state.is_guest) return false;
        const hidden_toggle_paths = new Set<string>([
            '/disclosure.html',
        ]);
        return !hidden_toggle_paths.has(location.pathname);
    })();

    return (
        <>
            <Navbar expand="lg" variant="dark" className={styles.Header} style={branding_styles}>
                <div className="d-flex justify-content-between align-items-center w-100 ps-3 pe-3">
                    <div className={'d-flex align-items-lg-end'}>
                        <ConditionalComponent show={show_toggle}>
                            <Button
                                className={'me-2 d-xl-none'}
                                variant={'nav-toggle'}
                                onClick={event => {
                                    event.preventDefault();
                                    actions.show_mobile_nav();
                                }}
                                title={'Navigation Toggle'}
                            >
                                <i className={`bi bi-list fs-1 ${styles.NavbarTextLinkBlue}`}
                                   style={{color: state.branding_header?.colors.links}}/>
                            </Button>
                        </ConditionalComponent>
                        <div>
                            <BrandingLogo
                                branding_header={!default_header_paths_found ? state.branding_header : undefined}
                                is_guest={state.is_guest}
                            />
                        </div>
                    </div>
                    <div className={'d-flex align-items-center'}>
                        <div className={`${styles.NavbarTextLink}  d-none d-xl-block`}>
                            <ConditionalComponent show={state.is_employee}>
                                    <Button
                                        type={'submit'}
                                        variant={'warning'}
                                        className={'rounded-pill px-4 bold fw-bold'}
                                    >COD Employee View</Button>
                            </ConditionalComponent>
                            <ConditionalComponent show={state.is_reguser && state.is_admin && !state.is_employee}>
                                <Nav className="me-auto">
                                    <Button
                                        type={'submit'}
                                        variant={'primary-gray'}
                                        className={'rounded-pill px-4'}
                                        onClick={() => navigate('/reguser/upgrade')}
                                    >Upgrade</Button>
                                </Nav>
                            </ConditionalComponent>
                        </div>
                        <div>
                            <ProfileDropdown/>
                        </div>
                    </div>
                </div>
            </Navbar>
            {state.branding_header?.contact && !default_header_paths_found ? (
                <ContactBanner
                    banner={state.branding_header.contact}
                    branding_language={state.branding_language}
                />
            ) : null}
        </>
    )
};

export default connector(Header);
